import type { Endomorphism } from '@code-expert/prelude';
import {
  adt,
  array,
  constant,
  eq,
  flow,
  iots,
  nat,
  nonEmptyArray,
  not,
  number,
  option,
  ord,
  pipe,
  readerIO,
  readerIOOption,
  struct,
} from '@code-expert/prelude';
import type { TimeEnv } from '/imports/utils/timeService';
import { SemesterId, semesterIdEq } from './identity';

const Season = iots.keyof({ AS: null, SS: null });

const foldSeason = adt.foldFromKeys(Season.keys);

export type Season = iots.TypeOf<typeof Season>;

export const SemesterC = iots.strict({
  id: SemesterId,
  enrollmentOpen: iots.boolean,
  locked: iots.boolean,
});

export type Semester = iots.TypeOf<typeof SemesterC>;

export const isPerpetualSemester = (id: SemesterId) => id === 'p';

export interface SeasonalSemester {
  season: Season;
  year: nat.Nat;
}

export const parseSeason: (_: SemesterId) => option.Option<Season> = flow(
  option.fromPredicate(not(isPerpetualSemester)),
  option.chain((id) => iots.parseOption(Season)(id.substring(0, 2))),
);

export const parseYear: (_: SemesterId) => option.Option<nat.Nat> = flow(
  option.fromPredicate(not(isPerpetualSemester)),
  option.chain((id) => iots.parseOption(iots.NatFromString)(id.substring(2, 4))),
);

export const parseSeasonalSemester = (semesterId: SemesterId): option.Option<SeasonalSemester> =>
  option.sequenceS({
    season: parseSeason(semesterId),
    year: parseYear(semesterId),
  });

export const seasonalSemesterId = ({ season, year }: SeasonalSemester): SemesterId =>
  `${season}${year}` as SemesterId;

export const currentSemester: readerIO.ReaderIO<TimeEnv, SeasonalSemester> =
  ({ time }) =>
  () => {
    const now = time.now();
    const season: Season = now.getMonth() <= 5 ? 'SS' : 'AS';
    const year = iots.parseSync(iots.Nat)(now.getFullYear() - 2000);
    return { season, year };
  };

export const incrementSemester: Endomorphism<SeasonalSemester> = ({ season, year }) =>
  pipe(
    season,
    foldSeason<SeasonalSemester>({
      SS: () => ({ season: 'AS', year }),
      AS: () => ({ season: 'SS', year: nat.succ(year) }),
    }),
  );

const getSeasonCardinality = foldSeason({
  SS: constant(0),
  AS: constant(1),
});

export const eqSemesters: eq.Eq<NonEmptyArray<Semester>> = nonEmptyArray.getEq(
  pipe(
    semesterIdEq,
    eq.contramap((s) => s.id),
  ),
);

export const ordSeasonalSemesterAsc: ord.Ord<SeasonalSemester> = ord.concatAll(
  pipe(nat.Ord, ord.contramap(struct.get('year'))),
  pipe(number.Ord, ord.contramap(flow(struct.get('season'), getSeasonCardinality))),
);

/**
 * Sort semesters, e.g. "AS19", "SS17", or "p"
 */
export const ordSemesterIdAsc: ord.Ord<SemesterId> = pipe(
  option.getOrd(ordSeasonalSemesterAsc),
  ord.contramap(parseSeasonalSemester),
);

export const ordSemesterIdDesc: ord.Ord<SemesterId> = ord.reverse(ordSemesterIdAsc);

export const makeNextSemester: (_: Array<SemesterId>) => readerIO.ReaderIO<TimeEnv, SemesterId> =
  flow(
    array.filterMap(parseSeasonalSemester),
    array.max(ordSeasonalSemesterAsc),
    readerIO.of,
    readerIOOption.getOrElse(() => currentSemester),
    readerIO.map(flow(incrementSemester, seasonalSemesterId)),
  );
